import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Facebook, Instagram, Phone, MapPin, Youtube, Video, MessageCircle, MessageSquare } from 'lucide-react';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    {
      name: 'Personal',
      href: '/',
      submenu: [
        { name: 'Internet', href: '/personal/internet' },
        { name: 'TV', href: '/personal/tv' },
        { name: 'Telefonie', href: '/personal/telefonie' },
        { name: 'Supraveghere', href: '/personal/supraveghere' },
        { name: 'Intercom', href: '/personal/intercom' }
      ]
    },
    {
      name: 'Business',
      href: '/business',
      submenu: [
        { name: 'Internet Business', href: '/business/internet' },
        { name: 'Hosting & Cloud', href: '/business/hosting' },
        { name: 'Rețele Private', href: '/business/networks' },
        { name: 'Securitate', href: '/business/security' }
      ]
    },
    {
      name: 'Shop',
      href: '/shop',
      submenu: [
        { name: 'Routere', href: '/shop/routers' },
        { name: 'Camere', href: '/shop/cameras' },
        { name: 'Interfoane', href: '/shop/intercoms' },
        { name: 'Accesorii', href: '/shop/accessories' }
      ]
    },
    {
      name: 'Mai multe',
      href: '#',
      submenu: [
        { name: 'Despre noi', href: '/about' },
        { name: 'Noutăți', href: '/news' },
        { name: 'Contacte', href: '/contact' },
        { name: 'Trimite un Mesaj', href: '/send-message' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-dark via-primary to-primary-light">
      {/* Header */}
      <header className="fixed w-full z-50" style={{ backgroundColor: '#ffffff' }}>
        <nav style={{ backgroundColor: '#ffffff' }}>
          <div className="container mx-auto px-4">
            <div className="flex justify-between items-center h-20" style={{ backgroundColor: '#ffffff' }}>
              <div className="flex items-center">
                <Link to="/">
                  <img 
                    src="/img/logo.png"
                    alt="Interlink Logo" 
                    className="h-16 w-auto object-contain"
                  />
                </Link>
              </div>
              
              <button 
                className="lg:hidden text-primary"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                style={{ backgroundColor: '#ffffff' }}
              >
                {isMenuOpen ? <X /> : <Menu />}
              </button>

              <nav className="hidden lg:flex space-x-8" style={{ backgroundColor: '#ffffff' }}>
                {navigation.map((item) => (
                  <div key={item.name} className="relative group">
                    <Link
                      to={item.href}
                      className={`text-primary hover:text-primary-dark transition-colors relative inline-flex items-center group ${
                        location.pathname === item.href || location.pathname.startsWith(item.href + '/') 
                          ? 'font-semibold' 
                          : ''
                      }`}
                    >
                      {item.name}
                      <ChevronDown className="ml-1 h-4 w-4" />
                      <span className={`absolute bottom-0 left-0 h-0.5 bg-primary transition-all duration-300 ${
                        location.pathname === item.href || location.pathname.startsWith(item.href + '/') 
                          ? 'w-full' 
                          : 'w-0 group-hover:w-full'
                      }`}></span>
                    </Link>
                    
                    {/* Submenu */}
                    <div className="absolute left-0 mt-2 w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 bg-white shadow-lg rounded-lg overflow-hidden">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.href}
                          to={subItem.href}
                          className={`block px-4 py-2 text-primary hover:bg-gray-50 ${
                            location.pathname === subItem.href ? 'bg-gray-50 font-semibold' : ''
                          }`}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </nav>
            </div>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t fixed top-20 left-0 right-0 bottom-0 overflow-y-auto" style={{ backgroundColor: '#ffffff' }}>
            <div className="container mx-auto px-4 py-4">
              {navigation.map((item) => (
                <div key={item.name} className="mb-4">
                  <Link
                    to={item.href}
                    className={`block py-2 text-primary font-semibold ${
                      location.pathname === item.href ? 'text-primary-dark' : ''
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                  <div className="ml-4 space-y-2">
                    {item.submenu.map((subItem) => (
                      <Link
                        key={subItem.href}
                        to={subItem.href}
                        className={`block py-1 text-primary ${
                          location.pathname === subItem.href ? 'font-semibold' : ''
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#0a2463] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Interlink Comunicații SRL</h3>
              <p className="text-gray-300">
                Furnizor de încredere de servicii de telecomunicații și siteme de securitate din Moldova
              </p>
              <div className="mt-4 space-y-2">
                <a 
                  href="https://www.facebook.com/interlink.md/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-gray-300 hover:text-white transition-colors"
                >
                  <Facebook className="h-5 w-5 mr-2" />
                  <span>Urmărește-ne pe Facebook</span>
                </a>
                <br />
                <a 
                  href="https://www.instagram.com/interlink_comunicatii" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-gray-300 hover:text-white transition-colors"
                >
                  <Instagram className="h-5 w-5 mr-2" />
                  <span>Urmărește-ne pe Instagram</span>
                </a>
                <br />
                <a 
                  href="https://www.youtube.com/@InterLinkComunicatii" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-gray-300 hover:text-white transition-colors"
                >
                  <Youtube className="h-5 w-5 mr-2" />
                  <span>Urmărește-ne pe YouTube</span>
                </a>
                <br />
                <a 
                  href="https://www.tiktok.com/@interlink.md" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-gray-300 hover:text-white transition-colors"
                >
                  <Video className="h-5 w-5 mr-2" />
                  <span>Urmărește-ne pe TikTok</span>
                </a>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Link-uri Rapide</h3>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-300 hover:text-white">Personal</Link></li>
                <li><Link to="/business" className="text-gray-300 hover:text-white">Business</Link></li>
                <li><Link to="/shop" className="text-gray-300 hover:text-white">Shop</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Suport Clienți</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-300 hover:text-white">FAQ</a></li>
                <li><a href="#" className="text-gray-300 hover:text-white">Suport Tehnic</a></li>
                <li><a href="#" className="text-gray-300 hover:text-white">Plăți Online</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Contact</h3>
              <div className="space-y-3">
                <div className="flex items-start text-gray-300">
                  <Phone className="h-5 w-5 mr-2 mt-1 flex-shrink-0" />
                  <div>
                    <a href="tel:+37323110000" className="hover:text-white block">
                      +373 231 10000
                    </a>
                    <a href="tel:+37322910910" className="hover:text-white block">
                      +373 22 910 910
                    </a>
                  </div>
                </div>
                <div className="flex items-start text-gray-300">
                  <MapPin className="h-5 w-5 mr-2 mt-1 flex-shrink-0" />
                  <a 
                    href="https://maps.app.goo.gl/2AvdigBc1Kj123F78" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="hover:text-white"
                  >
                    mun. Bălți, str. Decebal, 13
                  </a>
                </div>
                <div className="flex items-start text-gray-300">
                  <MessageCircle className="h-5 w-5 mr-2 mt-1 flex-shrink-0" />
                  <a 
                    href="https://t.me/interlink_telegram_bot" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-white"
                  >
                    Telegram
                  </a>
                </div>
                <div className="flex items-start text-gray-300">
                  <MessageSquare className="h-5 w-5 mr-2 mt-1 flex-shrink-0" />
                  <a 
                    href="https://m.me/interlink.md" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-white"
                  >
                    Messenger
                  </a>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Program de Lucru:</h4>
                  <p className="text-gray-300">Luni - Vineri: 08:00 - 17:00</p>
                  <p className="text-gray-300">Sâmbătă - Duminică: Închis</p>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-300">
              © {new Date().getFullYear()} Interlink Comunicații SRL. Toate drepturile rezervate.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;